/* eslint-disable max-lines */
import React, { FC, memo, useCallback, useMemo } from "react";
import { useTheme } from "styled-components";
import { useSelector } from "react-redux";
import { BarChart, LineChart, PieChart, PieChartData } from "@evergis/charts";
import { ITheme, ThemeProvider } from "@evergis/ui";

import { StackBar } from "../StackBar/StackBar";
import { ChartWrapper } from "./ChartWrapper";
import { PieChartCenter } from "../../containers/styled";
import { ChartLegendColor, ChartLegendValue, ChartTooltip, ChartTooltipRow } from "./styled";
import { AnyChartWrapper, BarChartContainer, BarChartWrapper } from "../Filters/styled";
import {
  DEFAULT_BARCHART_RADIUS,
  DEFAULT_CHART_ANGLE,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
  DEFAULT_PIECHART_RADIUS,
  MAX_CHART_WIDTH,
} from "../../constants";
import { DEFAULT_BAR_PADDING, DEFAULT_BAR_WIDTH } from "../Filters/constants";
import { getTotalFromAttributes } from "../../utils/getTotalFromAttributes";
import { getFormattedAttributes } from "../../utils/getFormattedAttributes";
import { pieChartTooltipFromAttributes } from "../../utils/pieChartTooltipFromAttributes";
import { tooltipNameFromAttributes } from "../../utils/tooltipNameFromAttributes";
import { tooltipValueFromAttributes } from "../../utils/tooltipValueFromAttributes";
import { pieChartTooltipFromRelatedFeatures } from "../../utils/pieChartTooltipFromRelatedFeatures";
import { tooltipValueFromRelatedFeatures } from "../../utils/tooltipValueFromRelatedFeatures";
import { getTotalFromRelatedFeatures } from "../../utils/getTotalFromRelatedFeatures";
import { getReferenceLayerInfos } from "../../../../ducks/showFeature/selectors";
import { formatAttributeValue } from "../../../../utils/formatAttributeValue";
import { isVisibleContainer } from "../../utils/isVisibleContainer";
import { isEmptyValue } from "../../utils/isEmptyValue";
import { getChartFilterName } from "../../utils/getChartFilterName";
import { getColorsFromFilterItems } from "./getColorsFromFilterItems";
import { getDataFromFilterItems } from "./getDataFromFilterItems";
import { getMinValueFromFilterItems } from "./getMinValueFromFilterItems";
import { getChartMarkers } from "../../utils/getChartMarkers";
import { roundTotalSum } from "../../utils/roundTotalSum";
import { useChartData } from "../../hooks/useChartData";
import { useChartChange } from "../../hooks/useChartChange";
import { useFeatureFilters } from "../../hooks/useFeatureFilters";
import { useWidgetContext } from "../../../Dashboard/hooks/useWidgetContext";
import { FeatureCardChartProps, FilterItem } from "../../types";

export const FeatureCardChart: FC<FeatureCardChartProps> = memo(
  ({ config, element, elementConfig, attributes, type, renderElement }) => {
    const theme = useTheme() as ITheme;
    const { palette } = theme;
    const { expandedContainers } = useWidgetContext(type);
    const layerInfos = useSelector(getReferenceLayerInfos);

    const { id, options, children } = element || {};
    const {
      column,
      markers: configMarkers,
      showTotal,
      expandable,
      expanded,
      chartType,
      relatedDataSources,
      defaultColor,
    } = options || {};
    const isLineChart = chartType === "line";
    const isPieChart = chartType === "pie";
    const isStackBar = chartType === "stack";
    const isBarChart = chartType === "bar";
    const height = options?.height || DEFAULT_CHART_HEIGHT;
    const radius = options?.radius || DEFAULT_BARCHART_RADIUS;
    const padding = options?.padding || DEFAULT_BAR_PADDING;
    const fontColor = options?.fontColor || palette.iconDisabled;
    const angle = options?.angle || DEFAULT_CHART_ANGLE;
    const barWidth = options?.barWidth || DEFAULT_BAR_WIDTH;
    const cornerRadius = options?.cornerRadius || 0;
    const primaryColor = theme.palette.primary;

    const relatedAttributes = useMemo(() => relatedDataSources || [], [relatedDataSources]);
    const axes = useMemo(() => relatedAttributes.filter(({ chartAxis }) => chartAxis === "y"), [relatedAttributes]);
    const filterName = getChartFilterName(relatedDataSources);

    const isRelated = !!relatedAttributes?.length;
    const { data, loading } = useChartData({
      config,
      element,
      attributes,
      type,
    });

    const totalWidthFromBarWidth = (data[0]?.items?.length || 0) * (barWidth + padding);
    const width = options?.width
      ? options.width > MAX_CHART_WIDTH
        ? MAX_CHART_WIDTH
        : options.width
      : isStackBar
      ? "100%"
      : isBarChart
      ? totalWidthFromBarWidth < MAX_CHART_WIDTH
        ? MAX_CHART_WIDTH
        : totalWidthFromBarWidth
      : DEFAULT_CHART_WIDTH;

    const [customize] = useChartChange({
      dataSources: config.dataSources,
      chartId: elementConfig?.id,
      width,
      height,
      fontColor,
      relatedAttributes,
      defaultColor: primaryColor,
    });

    const { formatFilterColor, onFilter } = useFeatureFilters(type, filterName, data?.[0]?.items as FilterItem[]);

    const formattedAttributes = useMemo(
      () => getFormattedAttributes((data[0]?.items || []) as FilterItem[], attributes, element),
      [attributes, data, element],
    );

    const totalSum = useMemo(
      () =>
        isRelated
          ? getTotalFromRelatedFeatures((data[0]?.items || []) as FilterItem[])
          : getTotalFromAttributes(children, formattedAttributes),
      [data, children, formattedAttributes, isRelated],
    );

    const totalWord = useMemo(
      () =>
        options?.totalAttribute
          ? (attributes.find(({ name }) => name === options.totalAttribute)?.value as number)
          : options?.totalWord,
      [attributes, options.totalAttribute, options?.totalWord],
    );

    const renderPieChartTooltip = useCallback(
      item =>
        isRelated
          ? pieChartTooltipFromRelatedFeatures(item, relatedAttributes, data[0]?.layerInfo)
          : pieChartTooltipFromAttributes(item, formattedAttributes),
      [data, formattedAttributes, isRelated, relatedAttributes],
    );

    const labels = useMemo(() => {
      if (!isLineChart) return [];

      const result = data?.map(item => (item?.items as PieChartData[])?.map(({ name }) => name?.toString()))?.flat();

      return result?.filter((name, index) => result.indexOf(name) === index);
    }, [data, isLineChart]);

    const renderLineChartTooltip = useCallback(
      (items, { indexX }) => {
        return (
          <ThemeProvider>
            <ChartTooltip>
              <div>{labels[indexX]}</div>
              {items
                .filter(({ value }) => !isEmptyValue(value))
                ?.map(({ layerName, attributeName, value, stroke }, index: number) => {
                  const layerInfo = layerInfos?.find(({ name }) => name === layerName);
                  const attribute = layerInfo?.layerDefinition.attributes[attributeName];

                  return (
                    <ChartTooltipRow key={index}>
                      <ChartLegendColor $color={stroke} />
                      <ChartLegendValue>
                        {attribute ? formatAttributeValue(attribute.type, value, attribute.stringFormat) : value}
                      </ChartLegendValue>
                    </ChartTooltipRow>
                  );
                })}
            </ChartTooltip>
          </ThemeProvider>
        );
      },
      [labels, layerInfos],
    );

    const markers = useMemo(
      () => getChartMarkers(data[0]?.items as FilterItem[], configMarkers),
      [data, configMarkers],
    );

    const formatTooltipName = useCallback(
      (name: string) => (isRelated ? name : tooltipNameFromAttributes(name, formattedAttributes)),
      [formattedAttributes, isRelated],
    );

    const formatTooltipValue = useCallback(
      (value, name) =>
        isRelated
          ? tooltipValueFromRelatedFeatures(value, relatedAttributes, data[0]?.layerInfo)
          : tooltipValueFromAttributes(value, name, formattedAttributes),
      [data, formattedAttributes, isRelated, relatedAttributes],
    );

    const renderChart = useMemo(() => {
      if (!element) return null;

      if (isLineChart) {
        const lineChartData = data.map(item => ({
          layerName: item.layerInfo?.name,
          attributeName: item.attributeName,
          stroke: item.color || primaryColor,
          values: (item.items as PieChartData[])
            ?.filter(({ value }) => !isEmptyValue(value))
            ?.map(({ value }) => Number(value)),
        }));
        const isHidedY = axes.every(({ hideAxis }) => hideAxis);
        const chartValues = lineChartData
          .flat()
          .map(({ values }) => values)
          .flat();

        return (
          <AnyChartWrapper height={height}>
            <LineChart
              data={lineChartData}
              labels={labels}
              width={+width}
              height={height}
              xAxisPadding={15}
              yAxisPadding={0}
              min={Math.min(...chartValues)}
              customYAxisSelection={yAxis => {
                if (isHidedY) {
                  yAxis.remove();
                }
              }}
              customYAxis={yAxis => yAxis.ticks(4)}
              renderTooltip={renderLineChartTooltip}
              customize={customize}
              dynamicTooltipEnable
              stackedTooltip
              drawGridX={!isHidedY}
              margin={{
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
              }}
            />
          </AnyChartWrapper>
        );
      }

      if (isStackBar) {
        return (
          <AnyChartWrapper height={height}>
            <StackBar
              data={data}
              filterName={filterName}
              type={type}
              alias={elementConfig?.children?.find(child => child.id === "alias")}
              options={options}
              renderTooltip={renderPieChartTooltip}
              renderElement={renderElement}
            />
          </AnyChartWrapper>
        );
      }

      if (isPieChart) {
        return (
          <AnyChartWrapper height={height}>
            <PieChart
              data={
                ((data[0]?.items as FilterItem[])
                  ?.filter(({ value }) => !isEmptyValue(value))
                  ?.map(item => ({
                    ...item,
                    color: formatFilterColor(item.name as string, item.color, defaultColor),
                  })) || []) as PieChartData[]
              }
              width={+width}
              height={+width}
              padAngle={angle}
              outerRadius={radius || DEFAULT_PIECHART_RADIUS}
              cornerRadius={cornerRadius}
              renderTooltip={renderPieChartTooltip}
              withTooltip
              onClick={filterName ? item => onFilter(item.name as string) : undefined}
            >
              {showTotal && <PieChartCenter>{totalWord || roundTotalSum(+totalSum)}</PieChartCenter>}
            </PieChart>
          </AnyChartWrapper>
        );
      }

      const margin = {
        top: 0,
        right: 0,
        bottom: markers?.length ? 20 : 0,
        left: 0,
      };

      return (
        <BarChartWrapper height={height ? height + margin.bottom : undefined}>
          <BarChartContainer>
            <BarChart
              data={getDataFromFilterItems(data[0]?.items as FilterItem[])}
              colors={getColorsFromFilterItems(data[0]?.items as FilterItem[], defaultColor, formatFilterColor)}
              minValue={getMinValueFromFilterItems(data[0]?.items as FilterItem[])}
              markers={markers}
              width={+width}
              height={height}
              barWidth={barWidth}
              barPadding={padding}
              customXAxis={axis => axis.remove()}
              customYAxis={axis => axis.remove()}
              customBars={({ bars }) => {
                bars.attr("rx", radius || DEFAULT_BARCHART_RADIUS);
                bars.attr("ry", radius || DEFAULT_BARCHART_RADIUS);
              }}
              margin={margin}
              xAxisPadding={0}
              yAxisPadding={0}
              formatTooltipValue={formatTooltipValue}
              formatTooltipName={formatTooltipName}
              onBarClick={filterName ? item => onFilter(item.name) : undefined}
              hideTooltipGroupName
              dynamicTooltipEnable
              isBarTooltip
            />
          </BarChartContainer>
        </BarChartWrapper>
      );
    }, [
      element,
      isLineChart,
      isStackBar,
      isPieChart,
      data,
      markers,
      height,
      defaultColor,
      formatFilterColor,
      width,
      barWidth,
      padding,
      formatTooltipValue,
      formatTooltipName,
      filterName,
      axes,
      labels,
      renderLineChartTooltip,
      customize,
      primaryColor,
      type,
      elementConfig?.children,
      options,
      renderPieChartTooltip,
      renderElement,
      angle,
      radius,
      cornerRadius,
      showTotal,
      totalWord,
      totalSum,
      onFilter,
    ]);

    if (!isVisibleContainer(id, expandable, expanded, expandedContainers)) return null;

    return (
      <ChartWrapper
        width={width}
        height={isStackBar ? "auto" : isPieChart ? width : height}
        column={column}
        loading={loading}
      >
        {renderChart}
      </ChartWrapper>
    );
  },
);
